<template>
    <div>
        <v-menu v-model="showMenu" :close-on-content-click="false">
            <template v-if="formats && formats.length" v-slot:activator="{props}">
                <v-btn color="primary" variant="text" icon="mdi-file-excel" v-bind="props" />
            </template>
            <v-card class="px-4 pt-2 pb-4">
                <v-radio-group v-model="radioGroup" density="compact">
                    <v-radio
                        v-for="radioItem in radioItems"
                        :key="radioItem.value"
                        :label="radioItem.label"
                        :disabled="radioItem.disabled"
                        :value="radioItem.value"
                    />
                </v-radio-group>
                <div class="d-table-row text-body-2">
                    <div class="d-table-cell pe-2">{{ $t('download') }}:</div>
                    <div class="d-table-cell">
                        <v-tooltip v-for="format in formats" :key="format.fileType" location="top">
                            <template v-slot:activator="{props}">
                                <v-btn
                                    variant="text"
                                    :disabled="isDisabled"
                                    :loading="loading"
                                    :icon="format.icon"
                                    color="primary"
                                    v-bind="props"
                                    @click="download(format)"
                                />
                            </template>
                            <span>{{ format.fileType }}</span>
                        </v-tooltip>
                    </div>
                </div>
            </v-card>
        </v-menu>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import {clean, clone, downloadData} from '@/utils/helpers'
    import {format} from 'date-fns'

    @Component
    export class OrdersDownloadMenu extends Vue {
        @Prop({required: true}) items
        @Prop() sortOrder

        showMenu = false
        radioGroup = ''
        loading = false

        async download(format) {
            this.loading = true
            try {
                const filters = await this.loadFilters()
                const data = await this.$api.downloadOrders.get({
                    fileType: format.fileType,
                    orderType: authStore.orderType,
                    ...filters,
                    ...(this.radioGroup === 'selected' && {orderIds: this.items.map(({orderId}) => orderId)}),
                })
                downloadData(data, `orders.${format.extension}`)
                this.radioGroup = ''
                this.showMenu = false
            } catch {
                this.$toast.error(this.$t('error_message.load_error'))
            }
            this.loading = false
        }

        loadFilters() {
            const filters = clone(persistentStore.orderFilters)

            if (this.sortOrder !== 'none') {
                filters.sortCriteria = 'earliestServiceDate'
                filters.sortOrder = this.sortOrder
                if (!filters.serviceStartDateFrom)
                    filters.serviceStartDateFrom = format(this.$dateFns.subYears(new Date(), 5), 'yyyy-MM-dd')
            }

            if (filters.orderId) {
                filters.orderId = parseInt(filters.orderId, 10)
            }
            if (filters.city) {
                if (filters.city?.type === 'country') {
                    filters.countryId = filters.city.id
                } else {
                    filters.cityId = filters.city.id
                }

                delete filters.city
            }
            if (filters.paymentMethod && filters.paymentMethod.name) {
                filters.paymentMethod = filters.paymentMethod.name
            }
            if (filters.supplier) {
                filters.supplierId = filters.supplier.id
                delete filters.supplier
            }
            if (filters.agent) {
                filters.agentId = filters.agent.id
                delete filters.agent
            }
            if (filters.reservationManager) {
                filters.managerId = filters.reservationManager.personId
                delete filters.reservationManager
            }
            if (filters.serviceManager) {
                filters.serviceManagerId = filters.serviceManager.personId
                delete filters.serviceManager
            }
            if (filters.customerCompany) {
                filters.customerCompany = filters.customerCompany.name
            }
            if (filters.client) {
                filters.clientId = filters.client.personId
                delete filters.client
            }
            if (filters.tripOrganizer) {
                filters.tripOrganizerId = filters.tripOrganizer.personId
                delete filters.tripOrganizer
            }
            if (filters.waitingApprovalFromMyself) {
                filters.authorizeUserId = this.person.personId
                delete filters.waitingApprovalFromMyself
            }
            if (filters.waitAuthorize === false) {
                delete filters.waitAuthorize
            }
            if (filters.noSupplierRef === false) {
                delete filters.noSupplierRef
            }
            if (filters.supplierInvoiceStatuses.length) {
                filters.supplierPaymentStatuses = [
                    ...filters.supplierPaymentStatuses,
                    ...filters.supplierInvoiceStatuses,
                ]
                delete filters.supplierInvoiceStatuses
            }
            if (filters.customerInvoiceStatuses.length) {
                filters.customerPaymentStatuses = [
                    ...filters.customerPaymentStatuses,
                    ...filters.customerInvoiceStatuses,
                ]
                delete filters.customerInvoiceStatuses
            }
            const addTime = (rq, date, time) => {
                if (rq[date]) {
                    rq[date] = this.$dateFns.format(rq[date]) + time
                }
            }
            addTime(filters, 'createdDateFrom', 'T00:00')
            addTime(filters, 'createdDateTo', 'T23:59')
            addTime(filters, 'serviceStartDateFrom', 'T00:00')
            addTime(filters, 'serviceStartDateTo', 'T23:59')
            addTime(filters, 'serviceEndDateFrom', 'T00:00')
            addTime(filters, 'serviceEndDateTo', 'T23:59')
            addTime(filters, 'modifiedDateFrom', 'T00:00')
            addTime(filters, 'modifiedDateTo', 'T23:59')
            addTime(filters, 'lastTicketingDateFrom', 'T00:00')
            addTime(filters, 'lastTicketingDateTo', 'T23:59')
            addTime(filters, 'customerPaymentDueDateFrom', 'T00:00')
            addTime(filters, 'customerPaymentDueDateTo', 'T00:00')
            addTime(filters, 'supplierPaymentDueDateFrom', 'T00:00')
            addTime(filters, 'supplierPaymentDueDateTo', 'T00:00')
            addTime(filters, 'manualBookingDateFrom', 'T00:00')
            addTime(filters, 'manualBookingDateTo', 'T23:59')
            clean(filters)

            return filters
        }

        get formats() {
            return [
                {
                    fileType: 'EXCEL',
                    icon: 'mdi-file-excel',
                    extension: 'xlsx',
                },
            ]
        }

        get radioItems() {
            return [
                {
                    value: 'selected',
                    label: `${this.$t('selected_orders')} (${this.items.length})`,
                    disabled: !this.items.length,
                },
                {value: 'all', label: this.$t('all_orders')},
            ]
        }

        get isDisabled() {
            return (!this.radioGroup && !this.items.length) || this.loading
        }
    }

    export default OrdersDownloadMenu
</script>
